import { useEffect, useState } from "react";
import { Compass, Focus, Footprints, TrendingDown, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ICON = { focus: Focus, smaller_wins: Footprints, taper: TrendingDown };
const TONE = { focus: "border-amber-200 bg-amber-50/60", smaller_wins: "border-emerald-200 bg-emerald-50/60", taper: "border-sky-200 bg-sky-50/60" };

/** Weekly "Strategy check": rules spot overload; the person decides. Nothing is ever applied without a tap. */
export function StrategyCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [taperStart, setTaperStart] = useState(3);
  const load = (refresh = false) => api.get(`/strategy${refresh ? "?refresh=1" : ""}`).then((r) => setData(r.data)).catch(() => setData({ suggestions: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const open = data.suggestions.filter((s) => s.status === "open");
  const act = async (s, action) => {
    setBusy(s.id);
    try {
      const { data: res } = await api.post(`/strategy/${s.id}/${action}`, action === "accept" ? { start: taperStart } : {});
      if (action === "accept") {
        toast.success(s.key === "focus" ? `${res.keystone} is your Keystone for the next two weeks.` : s.key === "taper" ? `Taper set: ${res.allowed} a day to start. It steps down each week you stay under.` : "Smaller win set — the goal dropped one rung.");
        onChanged?.();
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <section className="warm-card p-5" data-testid="strategy-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Compass className="w-3.5 h-3.5" /> Strategy check · week {data.week?.split("-W")[1]}</div>
          <h3 className="font-display text-lg font-bold text-stone-900 mt-1">{open.length ? "A gentler way through this stretch" : "Your plan looks balanced"}</h3>
          <p className="text-xs text-stone-600 mt-0.5">
            {open.length ? "Based on the last three weeks. Each idea is optional — tap to try it for two weeks, or dismiss." : `Checked ${data.habits_checked} habit${data.habits_checked === 1 ? "" : "s"}. Nothing to change; keep watering.`}
            {data.top_reason && <> Your reflections most often name <strong>{data.top_reason.replace(/_/g, " ")}</strong> as the obstacle.</>}
          </p>
        </div>
        <button onClick={() => load(true)} className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-900 hover:text-white flex items-center justify-center shrink-0" title="Re-run the check" data-testid="strategy-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      {open.length > 0 && (
        <ul className="mt-4 space-y-3">
          {open.map((s) => { const Icon = ICON[s.key]; return (
            <li key={s.id} className={`rounded-2xl border p-4 ${TONE[s.key]}`} data-testid={`strategy-suggestion-${s.key}`}>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-white/80 grid place-items-center shrink-0"><Icon className="w-4 h-4 text-stone-700" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-stone-900 text-sm">{s.title}</div>
                  <p className="text-sm text-stone-700 mt-1" data-testid={`strategy-text-${s.key}`}>{s.text}</p>
                  <p className="text-[11px] text-stone-500 mt-1">{s.why}{s.key === "focus" && s.others?.length > 0 && <> · Background for now: {s.others.join(", ")}.</>}</p>
                  {s.source === "template_pending_review" && <p className="text-[11px] text-amber-800 mt-1">Your coach is reviewing the AI wording; this is the standard version meanwhile.</p>}
                  {s.key === "taper" && (
                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-stone-700">Start allowance per day
                      <input type="number" min={1} max={50} value={taperStart} onChange={(e) => setTaperStart(Number(e.target.value))} className="w-16 px-2 py-1 rounded-lg border border-stone-300 text-sm" data-testid="strategy-taper-start" />
                    </label>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => act(s, "accept")} disabled={busy === s.id} className="amber-btn text-xs py-1.5 disabled:opacity-50" data-testid={`strategy-accept-${s.key}`}>Try this for 2 weeks</button>
                    <button onClick={() => act(s, "dismiss")} disabled={busy === s.id} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-white" data-testid={`strategy-dismiss-${s.key}`}><X className="w-3 h-3" /> Not now</button>
                  </div>
                </div>
              </div>
            </li>
          ); })}
        </ul>
      )}
    </section>
  );
}
